
import { useState } from 'react';
import { toast } from 'react-hot-toast';

/*
 * =======================================================================================
 * DEVELOPER CHECKLIST FOR IMPLEMENTING A NEW DELETE FLOW
 * =======================================================================================
 *
 * Purpose: This hook centralizes all delete logic, including confirmation modals,
 * logging, and error handling. Follow these steps to correctly implement a new delete flow.
 *
 * 1. IMPORT THE HOOK:
 *    - In the component that will trigger the deletion (e.g., StaffManagement.tsx):
 *      `import { useDeleteHandler } from '../../hooks/useDeleteHandler';`
 *      `import ConfirmationModal from '../ui/ConfirmationModal';`
 *
 * 2. INITIALIZE THE HOOK:
 *    - Call the hook at the top level of your component, providing the necessary configuration.
 *    - `onDeleteAsync` MUST be a function that takes the item to delete and performs the async
 *      operation (e.g., API call). It should throw an error on failure.
 *
 *    - Example:
 *      const {
 *          itemToDelete,
 *          isDeleting,
 *          initiateDelete,
 *          confirmDelete,
 *          cancelDelete,
 *      } = useDeleteHandler<StaffMember>({
 *          onDeleteAsync: async (staff) => onDeleteStaff(staff.id),
 *          entityName: 'Staff Member',
 *          getDisplayName: (staff) => staff.name,
 *          getItemId: (staff) => staff.id,
 *      });
 *
 * 3. WIRE UP THE TRIGGER:
 *    - Find the button or UI element that starts the delete process.
 *    - Attach the `initiateDelete` function to its `onClick` event.
 *    - Remember to use a memoized handler to comply with the `react/jsx-no-bind` ESLint rule.
 *
 *    - Example:
 *      const handleDeleteClick = useCallback((staff: StaffMember) => {
 *          initiateDelete(staff);
 *      }, [initiateDelete]);
 *
 *      <button onClick={() => handleDeleteClick(staffToDelete)}>Delete</button>
 *
 * 4. RENDER THE CONFIRMATION MODAL:
 *    - Add the `ConfirmationModal` component to your JSX.
 *    - It should be rendered conditionally based on the `itemToDelete` state from the hook.
 *    - Pass all required props from the hook to the modal.
 *
 *    - Example:
 *      {itemToDelete && (
 *          <ConfirmationModal
 *              title={`Delete ${entityName}`}
 *              message={`Are you sure you want to delete ${getDisplayName(itemToDelete)}?`}
 *              confirmButtonText="Delete"
 *              onConfirm={confirmDelete}
 *              onCancel={cancelDelete}
 *              isConfirming={isDeleting}
 *              // Optional: For "danger zone" actions
 *              // confirmationText="delete this item"
 *          />
 *      )}
 *
 * =======================================================================================
 */

interface UseDeleteHandlerProps<T> {
    onDeleteAsync: (item: T) => Promise<void>;
    entityName: string;
    getDisplayName: (item: T) => string;
    // A bit of a hack to get the ID, but needed for logging without forcing T to have an 'id'
    getItemId: (item: T) => string;
}

export const useDeleteHandler = <T,>({ onDeleteAsync, entityName, getDisplayName, getItemId }: UseDeleteHandlerProps<T>) => {
    const [itemToDelete, setItemToDelete] = useState<T | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const initiateDelete = (item: T) => {
        console.debug(`[DELETE_FLOW] ${entityName} delete initiated.`, { id: getItemId(item), name: getDisplayName(item) });
        setItemToDelete(item);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;

        console.debug(`[DELETE_FLOW] ${entityName} delete confirmed.`, { id: getItemId(itemToDelete), name: getDisplayName(itemToDelete) });
        setIsDeleting(true);

        try {
            await onDeleteAsync(itemToDelete);
            toast.success(`${entityName} "${getDisplayName(itemToDelete)}" has been deleted.`);
            console.debug(`[DELETE_FLOW] ${entityName} delete succeeded.`, { id: getItemId(itemToDelete) });
        } catch (error) {
            toast.error(`Failed to delete ${entityName}.`);
            console.error(`[DELETE_FLOW] ${entityName} delete failed.`, { id: getItemId(itemToDelete), error });
        } finally {
            setIsDeleting(false);
            setItemToDelete(null);
        }
    };

    const cancelDelete = () => {
        setItemToDelete(null);
    };

    return {
        itemToDelete,
        isDeleting,
        initiateDelete,
        confirmDelete,
        cancelDelete,
    };
};
